import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import { Box, Typography } from '@mui/material';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';

// Suppress findDOMNode warning from react-quill (known library issue)
// This is a temporary workaround until react-quill updates to remove findDOMNode usage
// Applied at module load time to catch all React warnings
(function suppressFindDOMNodeWarning() {
  const suppressedPattern = /findDOMNode/i;
  const originalWarn = console.warn;
  const originalError = console.error;
  
  const shouldSuppress = (args) => {
    return args.some(arg => {
      const message = typeof arg === 'string' ? arg : (arg?.message || arg?.toString() || '');
      return suppressedPattern.test(message);
    });
  };
  
  console.warn = function(...args) {
    if (shouldSuppress(args)) {
      return;
    }
    originalWarn.apply(console, args);
  };
  
  console.error = function(...args) {
    if (shouldSuppress(args)) {
      return;
    }
    originalError.apply(console, args);
  };
})();

const RichTextEditor = forwardRef(({
  value = '',
  onChange,
  label,
  placeholder = 'Enter text here...',
  error = false,
  helperText = '',
  disabled = false,
  required = false,
  maxHeight = '200px',
  fullWidth = true,
  margin = 'normal',
  id,
  onBlur,
  sx = {}
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const quillRef = useRef(null);
  
  // Forward the ref to the ReactQuill component
  useImperativeHandle(ref, () => quillRef.current, []);

  // Enhanced configuration with media support and image size handling
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['link', 'image', 'video']
    ]
  };

  const formats = ['bold', 'italic', 'underline', 'link', 'image', 'video', 'size'];

  // Simple change handler to prevent cursor jumping
  const handleChange = (content) => {
    const sanitizedContent = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'div', 'img', 'video', 'a', 'source'],
      ALLOWED_ATTR: ['src', 'alt', 'href', 'target', 'rel', 'width', 'height', 'controls', 'autoplay', 'muted', 'loop', 'poster', 'style'],
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      KEEP_CONTENT: true
    });
    
    const syntheticEvent = {
      target: {
        name: id || 'richTextEditor',
        value: sanitizedContent
      }
    };
    
    onChange?.(syntheticEvent);
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  return (
    <Box 
      sx={{ 
        width: fullWidth ? '100%' : 'auto', 
        margin: margin === 'normal' ? '16px 0 8px 0' : '8px 0'
      }}
    >
      {/* Label */}
      {label && (
        <Typography
          component="label"
          sx={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '400',
            color: error ? '#d32f2f' : '#050505',
            marginBottom: '8px',
            fontFamily: 'Lexend'
          }}
        >
          {label}
          {required && <span style={{ color: '#d32f2f' }}> *</span>}
        </Typography>
      )}

      {/* ReactQuill Editor */}
      <Box 
        className={`rich-text-editor ${isFocused ? 'focused' : ''} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
        sx={sx}
      >
        <ReactQuill
          ref={quillRef}
          key={id || 'rich-text-editor'}
          value={value || ''}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          theme="snow"
          style={{ 
            width: '100%',
            '--max-height': maxHeight
          }}
        />
      </Box>

      {/* Helper Text */}
      {helperText && (
        <Typography
          sx={{
            color: error ? '#d32f2f' : '#666666',
            fontSize: '12px',
            marginTop: '4px',
            fontFamily: 'Lexend'
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor; 